import { forceMutation, getPhase } from '../lib/fasterdom/fasterdom';
import { IS_MOBILE, IS_TOUCH_ENV } from './windowEnvironment';

const SCROLLABLE_SELECTOR = '.custom-scroll:not(.custom-scroll-x):not(.no-scrollbar)';
const BODY_CLASS = 'is-desktop-custom-scrollbars';
const LAYER_CLASS = 'custom-scrollbar-layer';
const THUMB_CLASS = 'custom-scrollbar-thumb';
const VISIBLE_CLASS = 'is-visible';
const IS_SCROLL_END_SUPPORTED = 'onscrollend' in window;
const IS_RESIZE_OBSERVER_SUPPORTED = 'ResizeObserver' in window;

const MIN_THUMB_SIZE = 24;
const TRACK_EDGE_GAP = 2;
const ACTIVE_CLASS_TIMEOUT_MS = 700;
const SCROLL_EPSILON = 1;
const THUMB_POSITION_EPSILON = 0.01;

let isInitialized = false;

interface OverlayScrollbarState {
  layer?: HTMLDivElement;
  thumb?: HTMLDivElement;
  isVisible?: boolean;
  hideTimeoutId?: number;
  isObserved?: boolean;
  isGeometryDirty?: boolean;
  maxScrollTop?: number;
  maxThumbTop?: number;
  currentThumbSize?: number;
  lastThumbSize?: number;
  lastThumbTop?: number;
}

const stateByElement = new WeakMap<HTMLElement, OverlayScrollbarState>();
const enabledElements = new Set<HTMLElement>();
const pendingUpdateElements = new Set<HTMLElement>();
let pendingUpdateRafId: number | undefined;
let resizeObserver: ResizeObserver | undefined;

function getState(element: HTMLElement) {
  let state = stateByElement.get(element);

  if (!state) {
    state = {};
    stateByElement.set(element, state);
  }

  return state;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function roundToPhysicalPixel(value: number) {
  const dpr = window.devicePixelRatio || 1;
  const step = 1 / dpr;
  return Math.round(value / step) * step;
}

function applyMutation(targets: Node | Node[], cb: NoneToVoidFunction) {
  if (getPhase() === 'measure') {
    forceMutation(cb, targets);
    return;
  }

  cb();
}

function markGeometryDirty(element: HTMLElement) {
  const state = getState(element);
  state.isGeometryDirty = true;
}

function observeElementGeometry(element: HTMLElement) {
  if (!resizeObserver) return;

  const state = getState(element);
  if (state.isObserved) return;

  resizeObserver.observe(element);
  state.isObserved = true;
}

function unobserveElementGeometry(element: HTMLElement, state: OverlayScrollbarState) {
  if (!resizeObserver || !state.isObserved) return;

  resizeObserver.unobserve(element);
  state.isObserved = undefined;
}

function cleanupElement(element: HTMLElement, state: OverlayScrollbarState) {
  enabledElements.delete(element);
  pendingUpdateElements.delete(element);
  clearOverlayHideTimeout(state);
  unobserveElementGeometry(element, state);
}

function ensureOverlay(element: HTMLElement) {
  const state = getState(element);
  if (state.layer && state.thumb && state.layer.isConnected) {
    enabledElements.add(element);
    observeElementGeometry(element);
    return true;
  }

  const layer = document.createElement('div');
  layer.className = LAYER_CLASS;
  const thumb = document.createElement('div');
  thumb.className = THUMB_CLASS;
  layer.appendChild(thumb);

  const hasRelativePosition = getComputedStyle(element).position === 'static';

  applyMutation([element, layer, thumb], () => {
    element.insertBefore(layer, element.firstChild);
    if (hasRelativePosition) {
      element.style.setProperty('position', 'relative');
    }
  });

  enabledElements.add(element);
  state.layer = layer;
  state.thumb = thumb;
  state.isObserved = undefined;
  state.isGeometryDirty = true;
  state.maxScrollTop = undefined;
  state.maxThumbTop = undefined;
  state.currentThumbSize = undefined;
  state.isVisible = false;
  state.lastThumbSize = undefined;
  state.lastThumbTop = undefined;
  observeElementGeometry(element);

  return true;
}

function clearOverlayHideTimeout(state: OverlayScrollbarState) {
  if (state.hideTimeoutId === undefined) return;

  window.clearTimeout(state.hideTimeoutId);
  state.hideTimeoutId = undefined;
}

function setOverlayVisibility(element: HTMLElement, isVisible: boolean) {
  const state = getState(element);
  const { layer } = state;
  if (state.isVisible === isVisible && layer && layer.isConnected) return;

  if (!isVisible) {
    clearOverlayHideTimeout(state);
  }

  state.isVisible = isVisible;
  if (!layer || !layer.isConnected) return;

  applyMutation(layer, () => {
    layer.classList.toggle(VISIBLE_CLASS, isVisible);
  });
}

function scheduleOverlayUpdate(element: HTMLElement) {
  pendingUpdateElements.add(element);

  if (pendingUpdateRafId !== undefined) return;

  pendingUpdateRafId = window.requestAnimationFrame(() => {
    pendingUpdateRafId = undefined;
    pendingUpdateElements.forEach((pendingElement) => {
      updateOverlay(pendingElement);
    });
    pendingUpdateElements.clear();
  });
}

function scheduleOverlayHide(element: HTMLElement) {
  if (IS_SCROLL_END_SUPPORTED) return;

  const state = getState(element);
  clearOverlayHideTimeout(state);

  state.hideTimeoutId = window.setTimeout(() => {
    state.hideTimeoutId = undefined;
    setOverlayVisibility(element, false);
  }, ACTIVE_CLASS_TIMEOUT_MS);
}

function recalculateGeometry(state: OverlayScrollbarState, clientHeight: number, scrollHeight: number) {
  const maxScrollTop = scrollHeight - clientHeight;
  state.maxScrollTop = maxScrollTop;

  if (maxScrollTop <= SCROLL_EPSILON || clientHeight <= 0) {
    state.currentThumbSize = 0;
    state.maxThumbTop = 0;
    return;
  }

  const trackSize = Math.max(0, clientHeight - TRACK_EDGE_GAP * 2);
  const maxThumbSize = trackSize > 0 ? trackSize : clientHeight;
  const rawThumbSize = (clientHeight * clientHeight) / scrollHeight;
  const thumbSize = Math.min(maxThumbSize, Math.max(MIN_THUMB_SIZE, rawThumbSize));

  state.currentThumbSize = roundToPhysicalPixel(thumbSize);
  state.maxThumbTop = Math.max(0, trackSize - state.currentThumbSize);
}

function updateOverlay(element: HTMLElement) {
  const state = getState(element);
  if (!element.isConnected) {
    cleanupElement(element, state);
    return;
  }
  if (!ensureOverlay(element)) return;
  const { thumb } = state;
  if (!thumb || !thumb.isConnected) return;
  const cachedMaxScrollTop = state.maxScrollTop;
  const rawScrollTop = element.scrollTop;
  // 提前读取 clientHeight 和 scrollHeight，以便检测 scrollHeight 变化
  // （ResizeObserver 只观察元素自身尺寸，不观察 scrollHeight，虚拟列表场景需要额外检测）
  const { clientHeight, scrollHeight } = element;
  const currentMaxScrollTop = scrollHeight - clientHeight;
  const needsGeometrySync = state.isGeometryDirty
    || state.maxScrollTop === undefined
    || state.maxThumbTop === undefined
    || state.currentThumbSize === undefined
    || (cachedMaxScrollTop !== undefined && rawScrollTop > cachedMaxScrollTop + SCROLL_EPSILON)
    || (cachedMaxScrollTop !== undefined
      && Math.abs(currentMaxScrollTop - cachedMaxScrollTop) > SCROLL_EPSILON);

  if (needsGeometrySync) {
    recalculateGeometry(state, clientHeight, scrollHeight);
    state.isGeometryDirty = false;
  }

  const maxScrollTop = state.maxScrollTop || 0;
  const scrollTop = clamp(rawScrollTop, 0, Math.max(maxScrollTop, 0));
  const thumbSize = state.currentThumbSize || 0;

  if (maxScrollTop <= SCROLL_EPSILON || thumbSize <= 0) {
    state.lastThumbSize = undefined;
    state.lastThumbTop = undefined;

    applyMutation(thumb, () => {
      thumb.style.height = '0px';
      thumb.style.transform = 'translate3d(0, 0, 0)';
    });

    setOverlayVisibility(element, false);
    return;
  }

  const maxThumbTop = state.maxThumbTop || 0;
  const scrollProgress = maxScrollTop > 0 ? scrollTop / maxScrollTop : 0;
  const thumbTop = roundToPhysicalPixel(TRACK_EDGE_GAP + scrollProgress * maxThumbTop);

  const isSizeChanged = state.lastThumbSize === undefined
    || Math.abs(state.lastThumbSize - thumbSize) >= THUMB_POSITION_EPSILON;
  const isTopChanged = state.lastThumbTop === undefined
    || Math.abs(state.lastThumbTop - thumbTop) >= THUMB_POSITION_EPSILON;

  if (!isSizeChanged && !isTopChanged) {
    return;
  }

  state.lastThumbSize = thumbSize;
  state.lastThumbTop = thumbTop;

  applyMutation(thumb, () => {
    if (isSizeChanged) {
      thumb.style.height = `${thumbSize}px`;
    }
    if (isTopChanged) {
      thumb.style.transform = `translate3d(0, ${thumbTop}px, 0)`;
    }
  });
}

function activateOverlay(element: HTMLElement) {
  ensureOverlay(element);
  setOverlayVisibility(element, true);
  scheduleOverlayUpdate(element);
  scheduleOverlayHide(element);
}

function handleScroll(event: Event) {
  const target = event.target;
  if (!(target instanceof HTMLElement) || !target.matches(SCROLLABLE_SELECTOR)) return;

  activateOverlay(target);
}

function handleScrollEnd(event: Event) {
  const target = event.target;
  if (!(target instanceof HTMLElement) || !target.matches(SCROLLABLE_SELECTOR)) return;

  setOverlayVisibility(target, false);
}

function handleWindowResize() {
  enabledElements.forEach((element) => {
    if (!element.isConnected) {
      cleanupElement(element, getState(element));
      return;
    }

    markGeometryDirty(element);
    scheduleOverlayUpdate(element);
  });
}

export default function initCustomScrollbars() {
  if (isInitialized || IS_MOBILE || IS_TOUCH_ENV) return;

  isInitialized = true;

  const init = () => {
    applyMutation([document.body, document.documentElement], () => {
      document.body.classList.add(BODY_CLASS);
      document.documentElement.style.setProperty('--scrollbar-width', '0px');
    });

    if (IS_RESIZE_OBSERVER_SUPPORTED) {
      resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const target = entry.target;
          if (!(target instanceof HTMLElement)) return;
          if (!target.matches(SCROLLABLE_SELECTOR)) return;

          markGeometryDirty(target);
          scheduleOverlayUpdate(target);
        });
      });
    }

    document.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    if (IS_SCROLL_END_SUPPORTED) {
      document.addEventListener('scrollend', handleScrollEnd, { capture: true, passive: true });
    }
    window.addEventListener('resize', handleWindowResize, { passive: true });
  };

  if (document.body) {
    init();
    return;
  }

  window.addEventListener('DOMContentLoaded', init, { once: true });
}
